import React, { useEffect, useRef, useState } from "react";
import { isProblematicInAppBrowser, getClientEnv } from "../lib/clientEnv";
import { postClientEvent } from "../lib/clientEnv";

// Iter 42.4 — Advisory banner shown inside Instagram / Facebook in-app
// browsers. Founder copy: "For smoother video uploads, open Gully in
// Chrome or Safari."
//
// Iter 42.5 (this file) — Adds a one-tap "Open in Chrome" button shown
// ONLY for Android Instagram/Facebook (no Chrome intent on iOS). The
// button fires an Android Chrome intent URL that preserves the current
// askgully.com path; if the intent doesn't catch within ~1.5s (i.e. the
// page is still alive), we copy the link to the clipboard as a fallback.
//
// Why this exists:
//   Instagram's Android WebView kills the tab during long XHR uploads
//   (50–100MB videos) — surfaces as the OS "Instagram isn't responding"
//   dialog. We can't fix that — only nudge users out into real Chrome
//   and measure how often the nudge works.
//
// Tracking events (POSTed to /api/web/client-error via postClientEvent):
//   - iab_banner_shown          (existing — one-shot per session)
//   - iab_banner_dismissed      (existing)
//   - open_in_chrome_shown      (one-shot per session; only when button is rendered)
//   - open_in_chrome_clicked    (every click)
//   - open_in_chrome_fallback_copy (when intent didn't navigate away and we copied URL)

const SS_KEY            = "gully_iab_banner_dismissed_v1";
const SS_SHOWN_KEY      = "gully_iab_banner_shown_v1";
const SS_CHROME_SHOWN   = "gully_open_in_chrome_shown_v1";
const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Build the Android Chrome intent URL for the *current* page so the user
// lands exactly where they were inside Instagram. Falls back to the
// origin if location.href is unparseable.
//
// Format reference (Android docs):
//   intent://<host>/<path>?<query>#Intent;scheme=https;package=com.android.chrome;end
function buildChromeIntentUrl() {
  try {
    const href = (typeof window !== "undefined" && window.location && window.location.href) || "";
    // Strip leading http:// or https:// — intent URL re-applies scheme via `scheme=`.
    const stripped = href.replace(/^https?:\/\//i, "");
    if (!stripped) return null;
    return `intent://${stripped}#Intent;scheme=https;package=com.android.chrome;end`;
  } catch { return null; }
}

export default function InAppBrowserBanner({ sessionId }) {
  const [visible, setVisible] = useState(false);
  const [hostApp, setHostApp] = useState(null);
  const [showChromeBtn, setShowChromeBtn] = useState(false);
  const [copied, setCopied] = useState(false);
  const postedRef = useRef(false);            // StrictMode double-effect guard (iab_banner_shown)
  const chromeShownPostedRef = useRef(false); // StrictMode guard (open_in_chrome_shown)

  useEffect(() => {
    if (!isProblematicInAppBrowser()) return;
    try {
      if (sessionStorage.getItem(SS_KEY) === "1") return;
    } catch { /* private mode — ignore */ }
    const env = getClientEnv();
    setHostApp(env.in_app_browser);
    setVisible(true);

    // The Chrome intent button is Android-only — iOS has no equivalent
    // and a fake button there would just confuse iPhone users (their
    // banner copy says "Safari", which is already the default action).
    const showBtn = env.os === "android";
    setShowChromeBtn(showBtn);

    // --- iab_banner_shown — one-shot per session ---
    if (!postedRef.current) {
      let alreadyShown = false;
      try { alreadyShown = sessionStorage.getItem(SS_SHOWN_KEY) === "1"; }
      catch { /* ignore */ }
      if (!alreadyShown) {
        postedRef.current = true;
        try { sessionStorage.setItem(SS_SHOWN_KEY, "1"); } catch { /* ignore */ }
        try {
          postClientEvent(API, sessionId, "iab_banner_shown", {
            host_app: env.in_app_browser,
          });
        } catch { /* swallow */ }
      } else {
        postedRef.current = true;
      }
    }

    // --- open_in_chrome_shown — one-shot per session, only when btn rendered ---
    if (showBtn && !chromeShownPostedRef.current) {
      let alreadyChromeShown = false;
      try { alreadyChromeShown = sessionStorage.getItem(SS_CHROME_SHOWN) === "1"; }
      catch { /* ignore */ }
      if (!alreadyChromeShown) {
        chromeShownPostedRef.current = true;
        try { sessionStorage.setItem(SS_CHROME_SHOWN, "1"); } catch { /* ignore */ }
        try {
          postClientEvent(API, sessionId, "open_in_chrome_shown", {
            host_app: env.in_app_browser,
          });
        } catch { /* swallow */ }
      } else {
        chromeShownPostedRef.current = true;
      }
    }
  }, [sessionId]);

  const dismiss = () => {
    try { sessionStorage.setItem(SS_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
    try {
      postClientEvent(API, sessionId, "iab_banner_dismissed", {
        host_app: hostApp,
      });
    } catch { /* swallow */ }
  };

  // Open-in-Chrome handler. Strategy:
  //   1. Always post `open_in_chrome_clicked` (best-effort, fire-and-forget).
  //   2. Navigate to the intent: URL. If Chrome catches it, the WebView
  //      unloads — we never reach the fallback.
  //   3. If the page is still alive after ~1500ms, treat as failure: copy
  //      the link to the clipboard so the user can paste it themselves,
  //      and post `open_in_chrome_fallback_copy` for observability.
  //   4. Listen for pagehide/visibilitychange to cancel the fallback
  //      timer — if the OS started swapping us out, the intent worked.
  const openInChrome = () => {
    const currentUrl = (typeof window !== "undefined" && window.location && window.location.href) || "";
    try {
      postClientEvent(API, sessionId, "open_in_chrome_clicked", {
        host_app: hostApp,
        url: currentUrl.slice(0, 240),
      });
    } catch { /* swallow */ }

    const intentUrl = buildChromeIntentUrl();
    if (!intentUrl) {
      // Nothing we can do — go straight to the copy fallback.
      runCopyFallback(currentUrl, "no_intent_url");
      return;
    }

    let fallbackFired = false;
    const fallbackTimer = setTimeout(() => {
      if (fallbackFired) return;
      fallbackFired = true;
      runCopyFallback(currentUrl, "intent_no_navigation");
    }, 1500);

    const cancelFallback = () => {
      fallbackFired = true; // suppress the copy
      clearTimeout(fallbackTimer);
    };
    // pagehide is the canonical "tab going away" signal cross-browser.
    window.addEventListener("pagehide", cancelFallback, { once: true });
    // visibilitychange catches Android task-switch into Chrome too.
    const onVis = () => {
      if (document.visibilityState === "hidden") cancelFallback();
    };
    document.addEventListener("visibilitychange", onVis, { once: true });

    try {
      // Setting location.href is what actually triggers the OS intent
      // resolver inside Instagram WebView.
      window.location.href = intentUrl;
    } catch {
      cancelFallback();
      runCopyFallback(currentUrl, "intent_threw");
    }
  };

  // Copy-to-clipboard fallback. Tries the modern Clipboard API first,
  // falls back to a hidden textarea + execCommand for ancient WebViews.
  // Always posts open_in_chrome_fallback_copy with the outcome.
  const runCopyFallback = (url, reason) => {
    const finish = (ok, extra) => {
      setCopied(ok);
      if (ok) setTimeout(() => setCopied(false), 4000);
      try {
        postClientEvent(API, sessionId, "open_in_chrome_fallback_copy", {
          host_app: hostApp,
          reason,
          copied: ok,
          ...(extra || {}),
        });
      } catch { /* swallow */ }
    };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(
          () => finish(true),
          (err) => finish(false, { error: String(err).slice(0, 120) }),
        );
        return;
      }
    } catch { /* fall through to execCommand */ }
    try {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand && document.execCommand("copy");
      document.body.removeChild(ta);
      finish(!!ok, { method: "execCommand" });
    } catch (err) {
      finish(false, { error: String(err).slice(0, 120) });
    }
  };

  if (!visible) return null;
  const appName = hostApp === "facebook" ? "Facebook"
                : hostApp === "instagram" ? "Instagram"
                : "this app";

  return (
    <div
      data-testid="iab-banner"
      data-host-app={hostApp || "unknown"}
      className="mx-4 mt-3 rounded-lg border border-amber-600/60 bg-amber-950/50 px-3 py-2 text-[13px] text-amber-200 flex items-start gap-3"
    >
      <span className="text-amber-300 mt-0.5" aria-hidden>⚠</span>
      <div className="flex-1">
        <p>
          For smoother video uploads, open Gully in
          {" "}<span className="text-amber-100 font-medium">Chrome</span>
          {" "}or{" "}
          <span className="text-amber-100 font-medium">Safari</span>.
        </p>
        <p className="text-amber-200/70 text-[11px] mt-1">
          {appName}&apos;s built-in browser can stop responding during long uploads.
        </p>
        {showChromeBtn && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              data-testid="iab-open-in-chrome-btn"
              onClick={openInChrome}
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-neutral-950 text-[12px] font-medium px-3 py-1.5 transition-colors"
            >
              Open in Chrome
              <span aria-hidden>→</span>
            </button>
            {copied && (
              <span
                data-testid="iab-open-in-chrome-copied"
                className="text-[11px] text-emerald-300"
              >
                Link copied — paste it in Chrome
              </span>
            )}
          </div>
        )}
      </div>
      <button
        data-testid="iab-banner-dismiss"
        onClick={dismiss}
        className="text-amber-300/70 hover:text-amber-100 text-lg leading-none px-1"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
